import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@structural-climbing/auth'
import type { SubscriptionStatus } from '@structural-climbing/ui'
import type { ActiveCheckInEntry } from '../members/memberTypes'

export interface TraineeSubscription {
  id: string
  effective_status: SubscriptionStatus
  expires_at: string | null
  entries_remaining: number | null
  gym_plans?: { name: string; plan_type: string } | null
}

export interface TraineeChild {
  id: string
  first_name: string
  last_name: string | null
  assignment_id: string
  starts_at: string
  ends_at: string | null
  subscription: TraineeSubscription | null
}

export interface Trainee {
  user_id: string
  user_email: string
  user_name: string
  first_name: string | null
  last_name: string | null
  nickname: string | null
  avatar_ext: string | null
  avatar_updated_at: string | null
  flags: Record<string, boolean>
  /** Whether the adult themselves is assigned, as opposed to only their child. */
  assigned: boolean
  assignment_id: string | null
  starts_at: string | null
  ends_at: string | null
  subscription: TraineeSubscription | null
  children: TraineeChild[]
}

/** The trainer themselves: they climb here too and check themselves in. */
export interface TrainerSelf {
  user_id: string
  user_email: string
  user_name: string
  nickname: string | null
  avatar_ext: string | null
  avatar_updated_at: string | null
  flags: Record<string, boolean>
  subscription: TraineeSubscription | null
}

export function useMyTrainees() {
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [trainees, setTrainees] = useState<Trainee[]>([])
  // Assignments that have not started yet. Kept apart from the caseload so a
  // future member never gets a check-in button the server would refuse.
  const [upcoming, setUpcoming] = useState<Trainee[]>([])
  const [self, setSelf] = useState<TrainerSelf | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Null while unknown. False means the caller has no caseload of their own —
  // a gym owner, typically — which is an explanation to show, not a failure.
  const [isTrainer, setIsTrainer] = useState<boolean | null>(null)
  // Who is in the gym right now. The endpoint is already scoped to this
  // trainer's people server-side, so nothing here needs filtering.
  const [checkedInMap, setCheckedInMap] = useState<Map<string, ActiveCheckInEntry>>(new Map())
  const [childCheckedInMap, setChildCheckedInMap] = useState<Map<string, ActiveCheckInEntry>>(
    new Map(),
  )

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const [resp, activeResp] = await Promise.all([
        fetch(`${apiUrl}/my-trainees`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiUrl}/active-check-ins`, { headers: { Authorization: `Bearer ${token}` } }),
      ])
      if (resp.status === 403) {
        const body = (await resp.json().catch(() => ({}))) as { code?: string }
        if (body.code === 'NOT_A_TRAINER') {
          setIsTrainer(false)
          setTrainees([])
          setUpcoming([])
          setSelf(null)
          return
        }
      }
      if (!resp.ok) throw new Error(String(resp.status))
      setIsTrainer(true)
      const data = (await resp.json()) as {
        trainees: Trainee[]
        upcoming?: Trainee[]
        self?: TrainerSelf | null
      }
      setTrainees(data.trainees ?? [])
      setUpcoming(data.upcoming ?? [])
      setSelf(data.self ?? null)

      if (activeResp.ok) {
        const active = (await activeResp.json()) as {
          active_check_ins: {
            id: string
            user_id: string | null
            child_id: string | null
            is_guest: boolean
          }[]
        }
        const byUser = new Map<string, ActiveCheckInEntry>()
        const byChild = new Map<string, ActiveCheckInEntry>()
        for (const entry of active.active_check_ins ?? []) {
          const value = { checkInId: entry.id, isGuest: entry.is_guest }
          if (entry.child_id) byChild.set(entry.child_id, value)
          else if (entry.user_id) byUser.set(entry.user_id, value)
        }
        setCheckedInMap(byUser)
        setChildCheckedInMap(byChild)
      }
    } catch {
      // The page shows an error rather than an empty list: "you have nobody
      // assigned" and "we could not reach the server" are very different
      // messages to a trainer looking for their 9am.
      setError('load')
    } finally {
      setLoading(false)
    }
  }, [apiUrl, getAccessTokenSilently])

  useEffect(() => {
    void reload()
  }, [reload])

  return {
    trainees,
    upcoming,
    self,
    loading,
    error,
    isTrainer,
    reload,
    checkedInMap,
    childCheckedInMap,
  }
}
